import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useIntl, FormattedMessage, FormattedNumber } from 'react-intl';
import moment from 'logic/moment';
import { setExpirationDateTime } from 'logic/bookingForm';
import { statuses } from 'constants/booking';
import OmocomLogo from 'assets/img/omocom.png';
import UserInfo from './UserInfo';

const styles = makeStyles((theme) => ({
  omocom: {
    maxWidth: '60%',
    zIndex: '-1',

    [theme.breakpoints.down('md')]: {
      maxWidth: '70%',
    },
  },
  smarkLogo: {
    marginTop: '-15px',
    display: 'inline-block',
    maxWidth: '50px',
  },
}));

const NumberOfGuests = ({ number_of_guests }) => {
  if (number_of_guests <= 1) {
    return (
      <FormattedMessage
        id="bookingForm.guestsCount"
        defaultMessage="{value} guest"
        values={{
          value: number_of_guests,
        }}
      />
    );
  }

  return (
    <FormattedMessage
      id="bookingForm.guestsCountPlural"
      defaultMessage="{value} guests"
      values={{
        value: number_of_guests,
      }}
    />
  );
};

NumberOfGuests.propTypes = {
  number_of_guests: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const CleaningPolicy = ({ cleaning_policy }) => {
  if (cleaning_policy === 'guest') {
    return (
      <FormattedMessage
        id="bookingForm.respGuest"
        defaultMessage="Guest is responsible for cleaning the cottage"
      />
    );
  }
  if (cleaning_policy === 'external') {
    return (
      <FormattedMessage
        id="bookingForm.respExternal"
        defaultMessage="Cleaning via cleaning company, paid separately"
      />
    );
  }

  return (
    <FormattedMessage
      id="bookingForm.respHost"
      defaultMessage="Cleaning is done by host and included in the price"
    />
  );
};

CleaningPolicy.propTypes = {
  cleaning_policy: PropTypes.string,
};

const Info = ({ booking, isHost, cancellationPolicy, preview }) => {
  const classes = styles();
  const intl = useIntl();

  return (
    <>
      <Box p={2} boxShadow={5} className={classes.container}>
        <div className={classes.smarkLogo}>
          <img
            src="data:image/png;base64,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"
            className={classes.smarkLogo}
            alt="S-bokning"
          />
        </div>
        <Typography variant="subtitle2">
          <FormattedMessage id="bookingForm.checkIn" defaultMessage="Check in" />
        </Typography>
        <Typography paragraph>{moment(booking.check_in).format('LLLL')}</Typography>
        <Typography variant="subtitle2">
          <FormattedMessage id="bookingForm.checkOut" defaultMessage="Check out" />
        </Typography>
        <Typography paragraph>{moment(booking.check_out).format('LLLL')}</Typography>
        {isHost && (
          <>
            <Typography variant="subtitle2">
              <FormattedMessage
                id="bookingForm.expirationDate"
                defaultMessage="Guest must decide latest"
              />
            </Typography>
            <Typography paragraph>
              {preview &&
                moment(setExpirationDateTime(booking.booking_expiration_date)).format('LLLL')}
              {!preview && moment(booking.booking_expiration_date).format('LLLL')}
            </Typography>
          </>
        )}
        <Typography variant="subtitle2">
          <FormattedMessage
            id="bookingForm.maxNumberOfGuests"
            defaultMessage="Maximum number of guests"
          />
        </Typography>
        <Typography paragraph>
          <NumberOfGuests number_of_guests={booking.number_of_guests} />
        </Typography>
        <Typography variant="subtitle2">
          <FormattedMessage id="bookingForm.cleaning" defaultMessage="Cleaning" />
        </Typography>
        <Typography paragraph>
          <CleaningPolicy cleaning_policy={booking.cleaning_policy} />
        </Typography>
        {booking.additional_information && (
          <>
            <Typography variant="subtitle2">
              <FormattedMessage
                id="bookingForm.additionalInformation"
                defaultMessage="Additional information"
              />
            </Typography>
            <Typography paragraph>{booking.additional_information}</Typography>
          </>
        )}
        {cancellationPolicy && (
          <>
            <Typography variant="subtitle2">
              <FormattedMessage
                id="bookingForm.cancellationPolicy"
                defaultMessage="Cancellation policy"
              />
            </Typography>
            <Typography paragraph>
              <FormattedMessage
                id="bookingForm.cancellationPolicyDesc2"
                defaultMessage="Free cancellation no later than
                    {cancel_for_full_refund_in_days} days before the agreed date
                    of arrival, and with a cost of 50% of the rental
                    sum for cancellations no later than {cancel_for_half_refund_in_days}
                    days before the agreed date of arrival."
                values={{
                  cancel_for_full_refund_in_days: cancellationPolicy.cancel_for_full_refund_in_days,
                  cancel_for_half_refund_in_days: cancellationPolicy.cancel_for_half_refund_in_days,
                }}
              />
            </Typography>
          </>
        )}
        <Typography variant="subtitle2">
          <FormattedMessage id="bookingForm.totalPrice" defaultMessage="Total price" />
        </Typography>
        <Typography paragraph>
          <FormattedNumber value={booking.total_price / 100} /> SEK
        </Typography>
        {isHost && booking.mark_as_booked_if_accepted && (
          <>
            <Typography variant="subtitle2">
              <FormattedMessage id="Calendar" defaultMessage="Calendar" />
            </Typography>
            <Typography paragraph>
              <FormattedMessage
                id="bookingForm.markAsBooked"
                defaultMessage="Mark the period as booked in calendar if guest accepts the offer. In case guest cancels the booking, mark the period as free again"
              />
            </Typography>
          </>
        )}

        <Typography variant="subtitle2">
          <FormattedMessage id="Host" defaultMessage="Host" />
        </Typography>
        <Typography paragraph component="div">
          <UserInfo {...booking.Host} hidePhone={!booking.paid_at} />
        </Typography>
        <Typography variant="subtitle2">
          <FormattedMessage id="Guest" defaultMessage="Guest" />
        </Typography>
        <Typography paragraph component="div">
          <UserInfo {...booking.Guest} hidePhone={!booking.paid_at} />
        </Typography>
        {isHost && booking.Guest.type === 'SOFT' && (
          <Typography variant="caption">
            <FormattedMessage
              id="bookingForm.guestIsSoft"
              defaultMessage="The guest needs to provide more contact details
                  before they can accept and pay"
            />
          </Typography>
        )}
      </Box>
      {isHost && booking.omocom_id && (
        <Box p={2} pb={1} mt={2} boxShadow={5} display="flex">
          <Box flex={1}>
            <img src={OmocomLogo} className={classes.omocom} alt="omocom" />
          </Box>
          <Box flex={3}>
            <Typography variant="subtitle2">
              <FormattedMessage
                id="bookingForm.insured"
                defaultMessage="This booking is insured by Omocom with insurance number:"
              />
            </Typography>
            <Typography paragraph>{booking.omocom_id}</Typography>
            <Typography paragraph>
              <a
                href={intl.formatMessage(
                  {
                    id: 'bookingForm.insurance.link',
                    defaultMessage:
                      'https://external.omocom.se/claimsform?insuranceId={insuranceid}',
                  },
                  { insuranceid: booking.omocom_id },
                )}
                target="_blank"
                rel="noreferrer"
              >
                <FormattedMessage id="bookingForm.claim" defaultMessage="Report claim" />
              </a>
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

Info.propTypes = {
  booking: PropTypes.shape({
    Guest: PropTypes.shape({
      type: PropTypes.string,
    }).isRequired,
    omocom_id: PropTypes.string,
    Host: PropTypes.shape({}).isRequired,
    additional_information: PropTypes.string,
    booking_expiration_date: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.object,
    ]).isRequired,
    cancellationPolicy: PropTypes.shape({}),
    check_in: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])
      .isRequired,
    check_out: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])
      .isRequired,
    cleaning_policy: PropTypes.string.isRequired,
    status: PropTypes.oneOf(Object.values(statuses)),
    total_price: PropTypes.number.isRequired,
    number_of_guests: PropTypes.number.isRequired,
    mark_as_booked_if_accepted: PropTypes.bool,
    paid_at: PropTypes.string,
  }).isRequired,
  isHost: PropTypes.bool,
  preview: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }),
  cancellationPolicy: PropTypes.shape({
    name: PropTypes.string,
    cancel_for_full_refund_in_days: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cancel_for_half_refund_in_days: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

export default Info;
